/**
 * PMT2 Path Utilities for Frontend
 * 
 * These utilities ensure consistent path sanitization between frontend and backend
 * for Firestore document paths, preventing path length limit errors.
 */

/**
 * Sanitization options for document IDs
 */
interface SanitizeDocIdOptions {
  maxLength?: number;
  context?: string;
}

/**
 * Creates an MD5 hash of a string (browser-compatible)
 * @param input The input string to hash
 * @returns MD5 hash as hex string
 */
async function createMD5Hash(input: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(input);
  const hashBuffer = await crypto.subtle.digest('MD5', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Calculates byte length of a string in UTF-8 encoding
 * @param str The string to measure
 * @returns Byte length in UTF-8
 */
function getByteLength(str: string): number {
  return new TextEncoder().encode(str).length;
}

/**
 * Sanitizes a document ID for safe use in Firestore paths
 * 
 * Rules:
 * - Replace forward slashes (/) with underscores (_)
 * - Replace dots (.) with underscores (_) 
 * - Trim whitespace
 * - If byte length exceeds maxLength, truncate and append MD5 hash
 * 
 * @param docId The document ID to sanitize
 * @param options Sanitization options
 * @returns Sanitized document ID
 */
export async function sanitizeDocId(
  docId: string, 
  options: SanitizeDocIdOptions = {}
): Promise<string> {
  const { maxLength = 200, context } = options;
  
  // Basic sanitization
  let sanitized = docId
    .replace(/\//g, '_')
    .replace(/\./g, '_')
    .trim();
  
  // Check if truncation is needed
  if (getByteLength(sanitized) <= maxLength) {
    return sanitized;
  }
  
  // Generate MD5 hash for uniqueness
  const hash = await createMD5Hash(docId);
  const hashSuffix = `_${hash.substring(0, 8)}`;
  
  // Calculate available space for content
  const availableLength = maxLength - getByteLength(hashSuffix);
  
  // Truncate to fit within available space
  let truncated = sanitized;
  while (getByteLength(truncated) > availableLength && truncated.length > 0) {
    truncated = truncated.substring(0, truncated.length - 1);
  }
  
  const result = `${truncated}${hashSuffix}`;
  
  // Log truncation for debugging
  if (context) {
    console.log(`[PathSanitizer] Truncated ${context}: "${docId}" -> "${result}" (${getByteLength(result)} bytes)`);
  }
  
  return result;
}

/**
 * Synchronous version of sanitizeDocId for cases where crypto.subtle is not available
 * Uses a simple hash function instead of MD5
 * 
 * @param docId The document ID to sanitize
 * @param options Sanitization options
 * @returns Sanitized document ID
 */
export function sanitizeDocIdSync(
  docId: string, 
  options: SanitizeDocIdOptions = {}
): string {
  const { maxLength = 200, context } = options;
  
  // Basic sanitization
  let sanitized = docId
    .replace(/\//g, '_')
    .replace(/\./g, '_')
    .trim();
  
  // Check if truncation is needed
  if (getByteLength(sanitized) <= maxLength) {
    return sanitized;
  }
  
  // Generate simple hash for uniqueness (fallback)
  let hash = 0;
  for (let i = 0; i < docId.length; i++) {
    const char = docId.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  const hashSuffix = `_${Math.abs(hash).toString(16).substring(0, 8)}`;
  
  // Calculate available space for content
  const availableLength = maxLength - getByteLength(hashSuffix);
  
  // Truncate to fit within available space
  let truncated = sanitized;
  while (getByteLength(truncated) > availableLength && truncated.length > 0) {
    truncated = truncated.substring(0, truncated.length - 1);
  }
  
  const result = `${truncated}${hashSuffix}`;
  
  // Log truncation for debugging
  if (context) {
    console.log(`[PathSanitizer] Truncated ${context}: "${docId}" -> "${result}" (${getByteLength(result)} bytes)`);
  }
  
  return result;
}

/**
 * Validates if a Firestore path is within byte limits
 * @param path The complete Firestore path
 * @returns Validation result
 */
export function validateFirestorePath(path: string): {
  valid: boolean;
  message?: string;
  path: string;
  byteLength?: number;
} {
  const byteLength = getByteLength(path);
  const maxLength = 1500; // Firestore's limit
  
  if (byteLength <= maxLength) {
    return {
      valid: true,
      path,
      byteLength
    };
  }
  
  return {
    valid: false,
    message: `Path exceeds ${maxLength} byte limit: ${byteLength} bytes`,
    path,
    byteLength
  };
}

/**
 * Helper function to construct sanitized Firestore paths for PMT2 ledger
 * @param companyName The company name
 * @param asin The ASIN
 * @param dataElement The data element name
 * @returns Promise resolving to sanitized path components
 */
export async function sanitizePMT2LedgerPath(
  companyName: string,
  asin: string,
  dataElement: string
): Promise<{
  sanitizedAsin: string;
  sanitizedDataElement: string;
  fullPath: string;
}> {
  const sanitizedAsin = await sanitizeDocId(asin, { 
    maxLength: 100, 
    context: 'ledger-asin' 
  });
  
  const sanitizedDataElement = await sanitizeDocId(dataElement, { 
    maxLength: 100, 
    context: 'ledger-dataElement' 
  });
  
  const fullPath = `pmt2_ledger/${companyName}/${sanitizedAsin}/${sanitizedDataElement}`;
  
  // Validate the complete path
  const pathValidation = validateFirestorePath(fullPath);
  if (!pathValidation.valid) {
    console.warn(`[PathSanitizer] Path still too long after sanitization: ${pathValidation.message}`);
  }
  
  return {
    sanitizedAsin,
    sanitizedDataElement,
    fullPath
  };
}